import { IObservable, Subject } from '~/rage/utils';
import { IPhoneGpsLocation, IVector3, MapPointCategory } from '~/shared';
import { RpcService } from '../RpcService';
import { VarsService } from '../VarsService';
import { IGpsService } from './gpsServiceTypes';

export class GpsService implements IGpsService {
	private readonly categories = new Subject<MapPointCategory[]>([]);
	private readonly mapPoints = new Subject<
		Record<MapPointCategory, IPhoneGpsLocation[]>
	>({} as Record<MapPointCategory, IPhoneGpsLocation[]>);
	private readonly playerPosition = new Subject<IVector3>({ x: 0, y: 0, z: 0 });
	private readonly activeRoute = new Subject<IPhoneGpsLocation | null>(null);

	constructor(
		private readonly rpc: RpcService,
		private readonly vars: VarsService,
	) {
		this.vars
			.get(
				'phone:gps:points',
				{} as Record<MapPointCategory, IPhoneGpsLocation[]>,
			)
			.subscribe(
				(points: Record<MapPointCategory, IPhoneGpsLocation[]> | null) => {
					if (!points) {
						this.categories.next([]);
						this.mapPoints.next(
							{} as Record<MapPointCategory, IPhoneGpsLocation[]>,
						);
						return;
					}

					const categoriesSet = new Set<MapPointCategory>();

					Object.entries(points).forEach(([category, items]) => {
						categoriesSet.add(category as MapPointCategory);
						items.forEach((point) => {
							if (!point.category) {
								point.category = MapPointCategory.IMPORTANT;
							}
						});
					});

					this.categories.next(Array.from(categoriesSet));
					this.mapPoints.next(points);
				},
			);

		this.vars
			.get('phone:playerPosition', { x: 0, y: 0, z: 0 })
			.subscribe((position) => {
				this.playerPosition.next(position as IVector3);
			});
	}

	public getAvaliableCategories(): IObservable<MapPointCategory[]> {
		return this.categories;
	}

	public getMapPoints(): IObservable<
		Record<MapPointCategory, IPhoneGpsLocation[]>
	> {
		return this.mapPoints;
	}

	public getPlayerPosition(): IObservable<IVector3> {
		return this.playerPosition;
	}

	public getActiveRoute(): IObservable<IPhoneGpsLocation | null> {
		return this.activeRoute;
	}

	public async setTargetRoute(route: IPhoneGpsLocation): Promise<void> {
		await this.rpc.invokeClient('phone:setTargetRoute', {
			position: route.position,
		});

		this.activeRoute.next(route);
	}

	public async clearTargetRoute(): Promise<void> {
		await this.rpc.invokeClient('phone:clearTargetRoute');

		this.activeRoute.next(null);
	}
}
